import React, { useEffect, useState } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useCartContext } from '../../context/cartContext';
import { useNavigate } from 'react-router-dom';
import updateOrderAndCreateCards from '../../utils/updateOrderAndCreateCards';

const CheckoutForm = ({ orderId }) => {
  const { total_amount, shippingFee, clearCart } = useCartContext();

  const stripe = useStripe();
  const elements = useElements();

  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          toast.success('Zahlung erfolgreich!');
          setMessage('Zahlung erfolgreich!');

          break;
        case 'processing':
          setMessage('Deine Zahlung wird verarbeitet.');
          break;
        case 'requires_payment_method':
          toast.error(
            'Deine Zahlung war nicht erfolgreich, bitte versuche es erneut.'
          );
          setMessage(
            'Deine Zahlung war nicht erfolgreich, bitte versuche es erneut.'
          );
          break;
        default:
          toast.error('Etwas ist schiefgelaufen.');
          setMessage('Etwas ist schiefgelaufen.');
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const payload = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url:
          process.env.NODE_ENV === 'development'
            ? `http://localhost:5173/warenkorb/bezahlung?orderId=${orderId}`
            : `https://app.svisit.ch/warenkorb/bezahlung?orderId=${orderId}`,
      },
      redirect: 'if_required',
    });

    if (payload.error) {
      toast.error(payload.error.message);
      setMessage(payload.error.message);
    } else {
      toast.success('Zahlung erfolgreich.');
      setMessage('Zahlung erfolgreich.');
      setSucceeded(true);
      clearCart();
      updateOrderAndCreateCards(orderId, payload.paymentIntent.id);
      setTimeout(() => {
        navigate('/');
      }, 10000);
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: 'tabs',
  };

  return (
    <main>
      {succeeded ? (
        <>
          <div className="px-4 text-center sm:px-6 lg:px-8">
            <h3 className="mt-2 text-xl font-bold tracking-tight text-gray-900 sm:text-xl">
              Danke für deine Bestellung!
            </h3>
          </div>
          <div className="text-center px-6">
            <p>Deine Zahlung war erfolgreich!</p>
            <p>Du wirst gleich zur Startseite weitergeleitet.</p>
          </div>
        </>
      ) : (
        <>
          <p className="text-center mx-auto my-4 max-w-xl text-base text-gray-600">
            Der Gesamtbetrag beläuft sich auf: CHF{' '}
            {(total_amount + shippingFee) / 100}
          </p>
          <form
            className="px-4 py-6 rounded-lg border border-gray-200 bg-white shadow-xs"
            id="payment-form"
            onSubmit={handleSubmit}
          >
            <PaymentElement
              id="payment-element"
              options={paymentElementOptions}
            />
            <button
              className="mt-6 w-full rounded-md border border-transparent bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-500 focus:ring-offset-2 focus:ring-offset-gray-50"
              disabled={isLoading || !stripe || !elements}
              id="submit"
            >
              <span id="button-text">
                {isLoading ? (
                  <span className="inline-flex items-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-3 text-white text-center"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="white"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="white"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Verarbeitet
                  </span>
                ) : (
                  'Jetzt bezahlen'
                )}
              </span>
            </button>
            {/* Show any error or success messages */}
            {message && <div id="payment-message">{message}</div>}
          </form>
        </>
      )}
    </main>
  );
};

export default CheckoutForm;
